/**
 * Heartbeats current presence every 5s while the tab is visible, polls the
 * peer list every 5s, and reports my currently-focused field.
 *
 * Replaceable later with a Durable Object WebSocket (see
 * functions/lib/batch-workspace.ts) without changing the consumer API.
 */

import { useEffect, useRef, useState } from 'react';
import * as presenceApi from '../api/presence';
import type { PresencePeer } from '../types';

const HEARTBEAT_MS = 5_000;
const POLL_MS = 5_000;

export interface PresenceState {
  peers: PresencePeer[];
  setFocus: (batchId: string | null, field: string | null) => void;
}

export function usePresence(enabled: boolean): PresenceState {
  const [peers, setPeers] = useState<PresencePeer[]>([]);
  const focusRef = useRef<{ batchId: string | null; field: string | null }>({
    batchId: null,
    field: null,
  });

  useEffect(() => {
    if (!enabled) return;
    let mounted = true;
    let hbTimer: ReturnType<typeof setInterval> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const beat = async () => {
      if (document.hidden) return;
      try {
        await presenceApi.heartbeat(
          focusRef.current.batchId,
          focusRef.current.field,
        );
      } catch {
        // Best effort — silent.
      }
    };

    const poll = async () => {
      if (document.hidden) return;
      try {
        const r = await presenceApi.listPeers();
        if (!mounted) return;
        // Filter out self by user_id.
        setPeers(r.peers.filter((p) => p.user_id !== r.me));
      } catch {
        // Silent.
      }
    };

    beat();
    poll();
    hbTimer = setInterval(beat, HEARTBEAT_MS);
    pollTimer = setInterval(poll, POLL_MS);

    return () => {
      mounted = false;
      if (hbTimer) clearInterval(hbTimer);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [enabled]);

  const setFocus = (batchId: string | null, field: string | null) => {
    focusRef.current = { batchId, field };
  };

  return { peers, setFocus };
}
